import { conditionalRender } from '@repo/toolkit/conditional-render'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getOptions } from '../../../api/options/get-options'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTrips } from '../../../api/trips/get-trips'
import { JourneyFilters } from '../../../lib/types/models/product/journey'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { yearsFilterOptions } from '../../../views/pcr-personal-space/utils'

interface Props {
  filter?: JourneyFilters
  onFilterChange: (filter: JourneyFilters) => void
}

export function JourneysFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof JourneyFilters>(key: K, value: JourneyFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: options } = useQuery({
    queryKey: ['options', { version: 'LIGHT' }],
    queryFn: () => getOptions({ version: 'LIGHT' })
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places'],
    queryFn: () => getJourneyPlaces()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <MultiSelectInput<number>
        label={'Année'}
        variant={'primary'}
        options={yearsFilterOptions}
        selectedOptions={filter?.years?.map((year) => ({
          label: yearsFilterOptions.find((y) => y.value === year)?.label ?? '',
          value: year
        }))}
        onChange={(years) => setFilter('years', years)}
      />

      <MultiSelectInput
        label={'Périodes'}
        variant={'primary'}
        searchable
        options={periods?.data.map((period) => ({ label: period.name, value: period.name })) ?? []}
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data.find((p) => p.name === period)?.name ?? '',
          value: period
        }))}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <MultiSelectInput
        label={'Options'}
        variant={'primary'}
        searchable
        options={
          options?.data.map((option) => ({
            label: getTranslatedName(option),
            value: getTranslatedName(option)
          })) ?? []
        }
        selectedOptions={filter?.options?.map((item) => ({
          label: getTranslatedName(
            options?.data.find((option) => getTranslatedName(option) === item)
          ),
          value: item
        }))}
        onChange={(options) => setFilter('options', options)}
      />

      <MultiSelectInput
        label={'Voyages'}
        variant={'primary'}
        searchable
        options={
          trips?.data.map((trip) => ({
            label: TripProperties.getTripLabel(trip),
            value: trip.id
          })) ?? []
        }
        selectedOptions={filter?.tripIds?.map((tripId) => {
          const trip = trips?.data.find((trip) => trip.id === tripId)
          return {
            label: TripProperties.getTripLabel(trip),
            value: tripId
          }
        })}
        onChange={(tripIds) => setFilter('tripIds', tripIds)}
      />

      <MultiSelectInput
        label={'Lieux'}
        variant={'primary'}
        searchable
        options={
          journeyPlaces?.data.map((journeyPlace) => ({
            label: journeyPlace.name,
            value: journeyPlace.name
          })) ?? []
        }
        selectedOptions={filter?.journeyPlaces?.map((item) => ({
          label:
            journeyPlaces?.data.find((journeyPlace) => journeyPlace.name === item)?.name ?? '',
          value: item
        }))}
        onChange={(journeyPlaces) => setFilter('journeyPlaces', journeyPlaces)}
      />

      <Input
        type={'number'}
        placeholder={'Durée en jours'}
        placeholderVariant="dark"
        value={filter?.durationInDays ? +filter.durationInDays : undefined}
        errorMessage={conditionalRender(
          filter?.durationInDays && filter.durationInDays.find((d) => d <= 0) ? true : false,
          'La durée doit être supérieur à zéro'
        )}
        onChange={(e) =>
          setFilter('durationInDays', +e.target.value === 0 ? undefined : [+e.target.value])
        }
      />

      <MultiSelectInput
        label={'Pays'}
        variant={'primary'}
        searchable
        options={countries?.data.map((country) => ({ label: country.name, value: country.name }))}
        selectedOptions={filter?.countries?.map((country) => ({
          label: countries?.data?.find((c) => c.name === country)?.name ?? '',
          value: country
        }))}
        onChange={(countries) => setFilter('countries', countries)}
      />

      <MultiSelectInput
        label={'Formule'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: getTranslatedName(formula)
        }))}
        selectedOptions={filter?.formulas?.map((formula) => ({
          label: getTranslatedName(formulas?.data?.find((f) => getTranslatedName(f) === formula)),
          value: formula
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
      />
    </div>
  )
}
